// hooks/useClientEOICreate.js
import { useState, useRef, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import ClientEOIService from '../../../../services/ClientEOIService';
import ServiceHDSService from '../../../../services/ServiceDHSService';
import AuthService from '../../../../services/AuthService';

const frequencyMapping = {
    'HEURE': 'HEURE',
    'HEBDOMADAIRE': 'HEBDOMADAIRE',
    'MENSUEL': 'MENSUEL'
};

const frequencyLabels = {
    'HEURE': 'Horaire',
    'HEBDOMADAIRE': 'Hebdomadaire',
    'MENSUEL': 'Mensuel'
};

const useClientEOICreate = (professional, onSuccess, onHide) => {
    const [formData, setFormData] = useState({
        comments: '',
        contractDuration: 1,
        serviceId: '',
        serviceName: '',
        serviceLocation: '',
        subServiceId: '',
        subServiceName: '',
        serviceFee: '',
        frequency: '',
        desiredStartDate: '',
        quantity: '0'
    });

    const [uploadedImages, setUploadedImages] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingService, setIsLoadingService] = useState(false);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [allSubServiceOptions, setAllSubServiceOptions] = useState([]);
    const [filteredSubServiceOptions, setFilteredSubServiceOptions] = useState([]);
    const [selectedSubService, setSelectedSubService] = useState(null);
    const [calculatedAmount, setCalculatedAmount] = useState(0);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadErrors, setUploadErrors] = useState([]);

    const fileInputRef = useRef(null);

    // Formater la date pour le backend
    const formatDateForBackend = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Récupérer les détails du service
    const fetchServiceDetails = async (serviceId) => {
        if (!serviceId) return;

        setIsLoadingService(true);
        try {
            const serviceData = await ServiceHDSService.getServiceById(serviceId);
            setServiceDetails(serviceData);

            if (serviceData.subServices && serviceData.subServices.length > 0) {
                const formattedSubServices = serviceData.subServices.map(subService => ({
                    id: subService.id,
                    name: subService.name,
                    amount: subService.amount,
                    frequency: subService.frequency,
                    originalFrequency: subService.frequency
                }));
                setAllSubServiceOptions(formattedSubServices);
                filterSubServicesByProfessional(formattedSubServices, professional.subServices);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du service:', error);
            Swal.fire('Erreur', 'Impossible de charger les détails du service.', 'error');
        } finally {
            setIsLoadingService(false);
        }
    };

    // Filtrer les sous-services par professionnel - CORRECTION ICI
    const filterSubServicesByProfessional = (allSubServices, professionalSubServicesString) => {
        if (!professionalSubServicesString || !allSubServices) {
            setFilteredSubServiceOptions(allSubServices || []);
            return;
        }

        // CORRECTION: Utiliser la virgule comme séparateur au lieu du point-virgule
        const professionalSubServices = professionalSubServicesString.split(',').map(sub => sub.trim());

        console.log('Sous-services professionnel (split par virgule):', professionalSubServices);
        console.log('Tous les sous-services disponibles:', allSubServices);

        const filtered = allSubServices.filter(subService => {
            const match = professionalSubServices.includes(subService.name);
            console.log(`Sous-service "${subService.name}" match: ${match}`);
            return match;
        });

        console.log('Sous-services filtrés:', filtered);
        setFilteredSubServiceOptions(filtered);
    };

    // Parser les sous-services du professionnel - CORRECTION ICI
    const parseProfessionalSubServices = (subServicesString) => {
        if (!subServicesString) return [];
        // CORRECTION: Utiliser la virgule comme séparateur
        return subServicesString.split(',').map((subService, index) => ({
            id: index + 1,
            name: subService.trim(),
            originalName: subService.trim()
        }));
    };

    // Initialisation du formulaire
    useEffect(() => {
        if (professional) {
            console.log('Professional reçu:', professional);
            console.log('Sous-services du professionnel:', professional.subServices);

            const initializeForm = async () => {
                setFormData(prev => ({
                    ...prev,
                    serviceId: professional.serviceId?.toString() || '',
                    serviceName: professional.selectedService || '',
                    serviceLocation: `${professional.city}, ${professional.country}` || '',
                    quantity: '0'
                }));

                setSelectedSubService(null);
                setCalculatedAmount(0);
                setHasUserInteracted(false);
                setUploadErrors([]);

                if (professional.serviceId) {
                    await fetchServiceDetails(professional.serviceId);
                } else {
                    const professionalSubServices = parseProfessionalSubServices(professional.subServices);
                    console.log('Sous-services parsés (sans serviceId):', professionalSubServices);
                    setFilteredSubServiceOptions(professionalSubServices);
                    setAllSubServiceOptions(professionalSubServices);
                }
            };

            initializeForm();
        }
    }, [professional]);

    // ... le reste du code reste identique ...

    // Gestion des changements de champs
    const handleInputChange = (field, value) => {
        if (field === 'quantity') {
            if (!hasUserInteracted && value !== '0') {
                setHasUserInteracted(true);
                value = value === '0' ? '' : value;
            }

            if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
                const newFormData = {
                    ...formData,
                    [field]: value,
                    contractDuration: value === '' ? 1 : parseInt(value) || 1
                };

                setFormData(newFormData);

                if (selectedSubService && value !== '' && parseInt(value) >= 1) {
                    calculateAmount(selectedSubService, parseInt(value));
                } else {
                    setCalculatedAmount(0);
                    setFormData(prev => ({ ...prev, serviceFee: '' }));
                }
            }
        } else {
            const newFormData = {
                ...formData,
                [field]: value
            };
            setFormData(newFormData);
        }
    };

    // Gestion du changement de sous-service
    const handleSubServiceChange = (subService) => {
        setSelectedSubService(subService);
        const mappedFrequency = frequencyMapping[subService.frequency] || subService.frequency;

        setFormData(prev => ({
            ...prev,
            subServiceId: subService.id?.toString() || '',
            subServiceName: subService.name,
            frequency: mappedFrequency,
            quantity: hasUserInteracted ? prev.quantity : '0',
            contractDuration: 1
        }));

        if (formData.quantity !== '' && parseInt(formData.quantity) >= 1) {
            calculateAmount(subService, parseInt(formData.quantity));
        } else {
            setCalculatedAmount(0);
            setFormData(prev => ({ ...prev, serviceFee: '' }));
        }
    };

    // Calcul du montant
    const calculateAmount = (subService, quantity) => {
        if (!subService || !subService.amount || quantity < 1) {
            setCalculatedAmount(0);
            setFormData(prev => ({ ...prev, serviceFee: '' }));
            return;
        }

        const amount = subService.amount * quantity;
        setCalculatedAmount(amount);
        setFormData(prev => ({ ...prev, serviceFee: amount.toString() }));
    };

    // Obtenir le libellé de la durée
    const getDurationLabel = () => {
        switch (formData.frequency) {
            case 'MENSUEL':
                return 'Durée du contrat (mois)';
            case 'HEBDOMADAIRE':
                return 'Durée du contrat (semaines)';
            case 'HEURE':
                return "Durée du contrat (heures)";
            default:
                return 'Durée du contrat (en mois)';
        }
    };

    // Obtenir le placeholder de la durée
    const getDurationPlaceholder = () => {
        switch (formData.frequency) {
            case 'MENSUEL':
                return 'Ex: 3 pour 3 mois';
            case 'HEBDOMADAIRE':
                return 'Ex: 4 pour 4 semaines';
            case 'HEURE':
                return "Ex: 8 pour 8 heures";
            default:
                return 'Entrez la durée';
        }
    };

    // Valider une image
    const validateImage = (file) => {
        if (!file.type.startsWith('image/')) {
            throw new Error('Seules les images sont autorisées (JPG, PNG, GIF, WEBP)');
        }

        if (file.size > 10 * 1024 * 1024) {
            throw new Error('La taille du fichier ne doit pas dépasser 10 MB');
        }

        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(fileExt)) {
            throw new Error('Formats acceptés: JPG, PNG, GIF, WEBP');
        }

        return true;
    };

    // Gestion du drag & drop
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) {
            setIsDragging(true);
        }
    }, [isDragging]);

    const handleDrop = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        await processFiles(files);
    }, [uploadedImages]);

    // Traiter les fichiers
    const processFiles = async (files) => {
        if (uploadedImages.length + files.length > 3) {
            Swal.fire('Erreur', 'Vous ne pouvez pas uploader plus de 3 images.', 'error');
            return;
        }

        setIsUploading(true);
        const newErrors = [];

        for (const file of files) {
            try {
                validateImage(file);

                setUploadedImages(prev => [...prev, {
                    file: file,
                    name: `image_${Date.now()}`,
                    preview: URL.createObjectURL(file),
                    username: AuthService.getCurrentUser()?.username || 'user'
                }]);
            } catch (error) {
                newErrors.push(error.message);
            }
        }

        if (newErrors.length > 0) {
            setUploadErrors(newErrors);
            Swal.fire('Erreur', newErrors.join('\n'), 'error');
            setTimeout(() => setUploadErrors([]), 5000);
        }

        setIsUploading(false);
    };

    // Gestion de l'upload via input
    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        await processFiles(files);
        event.target.value = '';
    };

    // Supprimer une image
    const removeImage = (index) => {
        const imageToRemove = uploadedImages[index];
        if (imageToRemove.preview) {
            URL.revokeObjectURL(imageToRemove.preview);
        }
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    // Soumission du formulaire
    const handleSubmit = async () => {
        try {
            const currentUser = AuthService.getCurrentUser();

            if (!currentUser) {
                Swal.fire('Erreur', 'Vous devez être connecté pour envoyer une demande.', 'error');
                return;
            }

            // Validations
            if (!formData.serviceId || !formData.serviceLocation || !formData.desiredStartDate ||
                !formData.comments || !formData.subServiceName) {
                Swal.fire('Erreur', 'Veuillez remplir tous les champs obligatoires.', 'error');
                return;
            }

            if (!formData.quantity || formData.quantity === '' || parseInt(formData.quantity) < 1) {
                Swal.fire('Erreur', 'La durée du contrat doit être égale ou supérieure à 1.', 'error');
                return;
            }

            if (!formData.serviceFee || calculatedAmount === 0) {
                Swal.fire('Erreur', 'Veuillez sélectionner un sous-service et spécifier la durée du contrat.', 'error');
                return;
            }

            setIsSubmitting(true);

            const eoiData = {
                comments: formData.comments,
                clientEOIStatus: "PENDING",
                userValidated: null,
                userRejected: null,
                rejectionReason: null,
                clientId: currentUser.id,
                professionalId: professional.id,
                contractDuration: formData.contractDuration,
                serviceId: parseInt(formData.serviceId),
                serviceName: formData.serviceName,
                serviceLocation: formData.serviceLocation,
                subServiceId: formData.subServiceId ? parseInt(formData.subServiceId) : null,
                subServiceName: formData.subServiceName,
                serviceFee: parseFloat(formData.serviceFee),
                frequency: formData.frequency,
                desiredStartDate: formatDateForBackend(formData.desiredStartDate),
                createdBy: currentUser.username,
                lastModifiedBy: currentUser.username,
                mediaSubscriptions: uploadedImages.map(img => ({
                    name: img.name,
                    username: img.username,
                    url: img.preview,
                    createdBy: currentUser.username
                }))
            };

            const mediaFiles = uploadedImages.map(img => img.file);
            await ClientEOIService.createClientEOI(eoiData, mediaFiles);

            Swal.fire('Succès !', 'Votre demande a été envoyée avec succès.', 'success');
            resetForm();
            if (onSuccess) onSuccess();
            onHide();

        } catch (error) {
            console.error('Error submitting interest:', error);
            const errorMessage = error.response?.data?.message || error.message ||
                'Une erreur est survenue lors de l\'envoi de votre demande.';
            Swal.fire('Erreur !', errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Réinitialisation du formulaire
    const resetForm = () => {
        uploadedImages.forEach(img => {
            if (img.preview) {
                URL.revokeObjectURL(img.preview);
            }
        });

        setFormData({
            comments: '',
            contractDuration: 1,
            serviceId: '',
            serviceName: '',
            serviceLocation: '',
            subServiceId: '',
            subServiceName: '',
            serviceFee: '',
            frequency: '',
            desiredStartDate: '',
            quantity: '0'
        });
        setUploadedImages([]);
        setServiceDetails(null);
        setAllSubServiceOptions([]);
        setFilteredSubServiceOptions([]);
        setSelectedSubService(null);
        setCalculatedAmount(0);
        setHasUserInteracted(false);
        setUploadErrors([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Gestion de la fermeture
    const handleClose = () => {
        if (uploadedImages.length > 0) {
            Swal.fire({
                title: 'Êtes-vous sûr ?',
                text: 'Les images uploadées seront perdues.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Oui, annuler',
                cancelButtonText: 'Non, continuer'
            }).then((result) => {
                if (result.isConfirmed) {
                    uploadedImages.forEach(img => {
                        if (img.preview) {
                            URL.revokeObjectURL(img.preview);
                        }
                    });
                    resetForm();
                    onHide();
                }
            });
        } else {
            resetForm();
            onHide();
        }
    };

    return {
        // États
        formData,
        uploadedImages,
        isUploading,
        isSubmitting,
        isLoadingService,
        serviceDetails,
        allSubServiceOptions,
        filteredSubServiceOptions,
        selectedSubService,
        calculatedAmount,
        hasUserInteracted,
        isDragging,
        uploadErrors,
        fileInputRef,
        frequencyLabels,

        // Gestionnaires
        handleInputChange,
        handleSubServiceChange,
        handleImageUpload,
        removeImage,
        handleSubmit,
        handleClose,
        resetForm,
        getDurationLabel,
        getDurationPlaceholder,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
        processFiles
    };
};

export default useClientEOICreate;